import { db } from '../firebase';
import { collection, doc, writeBatch, getDocs, query, where, serverTimestamp, updateDoc } from 'firebase/firestore';
import Papa from 'papaparse';
import { GrossReceiptRecord, ShortfallAllocation, MasterTransaction, LaborRecord } from '../types';
import { getStaff } from './staffService';

const normalizeDate = (d: string): string => {
  if (!d) return '';
  const clean = d.trim();
  if (clean.includes('/')) {
    const parts = clean.split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      let year = parts[2];
      if (year.length === 2) {
        year = '20' + year;
      }
      return `${year}-${month}-${day}`;
    }
  }
  if (clean.includes('-')) {
    const parts = clean.split('-');
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
      } else if (parts[2].length === 4) {
        return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
    }
  }
  return clean;
};

export const getGrossReceiptsForPeriod = async (tenantId: string, startDate: string, endDate: string): Promise<GrossReceiptRecord[]> => {
  const receiptsRef = collection(db, 'tenants', tenantId, 'gross_receipts');
  const q = query(
    receiptsRef,
    where('date', '>=', startDate),
    where('date', '<=', endDate)
  );
  
  try {
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as GrossReceiptRecord));
  } catch (error) {
    console.error('Error fetching gross receipts:', error);
    return [];
  }
};

export const importGrossReceiptsCSV = async (tenantId: string, file: File, filename: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      delimiter: ",",
      complete: async (results) => {
        const batch = writeBatch(db);
        let added = 0;
        
        for (const row of results.data as any[]) {
          const rawDate = row['Date'] || row['Service Date'] || row['Business Date'] || '';
          const date = normalizeDate(rawDate);
          
          const gross = Number(row['Gross Receipts'] || row['Gross Sales'] || row['Sales'] || 0);
          const chargeSales = Number(row['Charge Receipts'] || row['Card Sales'] || row['Credit Sales'] || 0);
          const chargeTips = Number(row['Charge Tips'] || row['Credit Card Tips'] || row['Card Tips'] || 0);
          
          if (!date || isNaN(gross)) continue;
          
          const ref = doc(db, 'tenants', tenantId, 'gross_receipts', date);
          batch.set(ref, {
            tenantId,
            date,
            grossReceipts: gross,
            chargeReceipts: chargeSales,
            chargeTips: chargeTips,
            importedAt: serverTimestamp(),
            filename
          } as GrossReceiptRecord);
          
          added++;
        }
        
        if (added > 0) {
          await batch.commit();
        }
        resolve(added);
      },
      error: reject
    });
  });
};

export interface ShortfallCalculationResult {
  totalGrossReceipts: number;
  totalChargeReceipts: number;
  totalChargeTips: number;
  targetTips: number;
  totalReportedTips: number;
  shortfall: number;
  allocations: ShortfallAllocation[];
}

export const calculateShortfall = async (
  tenantId: string,
  startDate: string,
  endDate: string,
  method: 'hours_worked' | 'gross_receipts' | 'good_faith',
  rate: number = 0.08,
  employeeGrossReceiptsInput?: Record<string, number>
): Promise<ShortfallCalculationResult> => {
  // 1. Get gross receipts and calculate totals
  const receipts = await getGrossReceiptsForPeriod(tenantId, startDate, endDate);
  const totalGrossReceipts = receipts.reduce((sum, r) => sum + (r.grossReceipts || 0), 0);
  const totalChargeReceipts = receipts.reduce((sum, r) => sum + (r.chargeReceipts || 0), 0);
  const totalChargeTips = receipts.reduce((sum, r) => sum + (r.chargeTips || 0), 0);
  
  const targetTips = totalGrossReceipts * rate;
  
  // 2. Fetch staff list
  const staffList = await getStaff(tenantId);
  
  // 3. Fetch all master transactions for reported tips
  const txRef = collection(db, 'tenants', tenantId, 'master_transactions');
  const txQ = query(
    txRef,
    where('tipDate', '>=', startDate),
    where('tipDate', '<=', endDate)
  );
  
  const txSnapshot = await getDocs(txQ);
  const transactions = txSnapshot.docs.map(doc => doc.data() as MasterTransaction);
  
  // Aggregate reported tips per staff member
  const reportedTipsByStaff: Record<string, number> = {};
  transactions.forEach(tx => {
    // Only count standard reported tips, not previously calculated shortfall adjustments or service charges
    if (tx.source !== 'Calculated' && !tx.isServiceCharge) {
      const amount = tx.amount || tx.allocatedAmount || tx.submittedAmount || 0;
      reportedTipsByStaff[tx.staffId] = (reportedTipsByStaff[tx.staffId] || 0) + amount;
    }
  });
  
  const totalReportedTips = Object.values(reportedTipsByStaff).reduce((sum, amt) => sum + amt, 0);
  const shortfall = targetTips - totalReportedTips;
  
  if (shortfall <= 0) {
    return {
      totalGrossReceipts,
      totalChargeReceipts,
      totalChargeTips,
      targetTips,
      totalReportedTips,
      shortfall: 0,
      allocations: []
    };
  }
  
  // 4. Perform employee allocations based on method
  const allocations: ShortfallAllocation[] = [];
  const batchId = `sf_${Date.now()}`;
  
  if (method === 'hours_worked') {
    // Fetch all labor records in this date range
    const laborRef = collection(db, 'tenants', tenantId, 'labor_records');
    const laborQ = query(
      laborRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );
    const laborSnapshot = await getDocs(laborQ);
    const laborRecords = laborSnapshot.docs.map(doc => doc.data() as LaborRecord);
    
    // Sum hours per employee, merging labor records and POS transactions
    const hoursByStaff: Record<string, number> = {};
    
    // Create a nested map: staffId -> date -> shiftName -> hours
    const staffShiftHours: Record<string, Record<string, Record<string, number>>> = {};
    
    // Helper to register hours
    const registerHours = (staffId: string, date: string, shiftName: string, hours: number) => {
      const cleanShift = (shiftName || 'General').trim().toLowerCase();
      if (!staffShiftHours[staffId]) {
        staffShiftHours[staffId] = {};
      }
      if (!staffShiftHours[staffId][date]) {
        staffShiftHours[staffId][date] = {};
      }
      staffShiftHours[staffId][date][cleanShift] = Math.max(
        staffShiftHours[staffId][date][cleanShift] || 0,
        hours
      );
    };
    
    // 1. Populate from manual labor records
    laborRecords.forEach(rec => {
      if (rec.staffId && rec.date && rec.hoursWorked) {
        registerHours(rec.staffId, rec.date, rec.shiftName || 'General', rec.hoursWorked);
      }
    });
    
    // 2. Populate from master transactions (POS)
    transactions.forEach(tx => {
      if (tx.staffId && tx.tipDate && tx.hoursWorked && (tx.source === 'POS' || tx.source === 'POS Override')) {
        registerHours(tx.staffId, tx.tipDate, tx.shiftName || 'General', tx.hoursWorked);
      }
    });
    
    // 3. Sum everything up for each staff member
    Object.entries(staffShiftHours).forEach(([staffId, dates]) => {
      let total = 0;
      Object.values(dates).forEach(shifts => {
        Object.values(shifts).forEach(hours => {
          total += hours;
        });
      });
      hoursByStaff[staffId] = total;
    });
    
    // Filter staff who worked during the period
    const activeStaff = staffList.filter(s => (hoursByStaff[s.id!] || 0) > 0);
    const totalHours = Object.values(hoursByStaff).reduce((sum, h) => sum + h, 0);
    
    if (totalHours > 0) {
      activeStaff.forEach(staff => {
        const hours = hoursByStaff[staff.id!] || 0;
        const sharePercentage = hours / totalHours;
        const allocatedShortfall = shortfall * sharePercentage;
        
        allocations.push({
          tenantId,
          startDate,
          endDate,
          staffId: staff.id!,
          staffName: staff.name,
          email: staff.email,
          hoursWorked: hours,
          sharePercentage,
          allocatedShortfall,
          allocationMethod: 'hours_worked',
          createdAt: new Date().toISOString(), // Fallback
          batchId,
          source: 'Short-Fall',
          type: 'true_up',
          reportingStatus: 'true_up_allocated'
        });
      });
    }
  } else if (method === 'gross_receipts') {
    // Use employee-specific gross receipts (e.g., individual sales)
    const input = employeeGrossReceiptsInput || {};
    
    // Fallback to active staff from transactions if input is empty
    const activeStaff = staffList.filter(s => (input[s.id!] || 0) > 0 || (reportedTipsByStaff[s.id!] || 0) > 0);
    const totalEmployeeGross = activeStaff.reduce((sum, s) => sum + (input[s.id!] || 0), 0);
    
    if (totalEmployeeGross > 0) {
      activeStaff.forEach(staff => {
        const empGross = input[staff.id!] || 0;
        const sharePercentage = empGross / totalEmployeeGross;
        const allocatedShortfall = shortfall * sharePercentage;
        
        allocations.push({
          tenantId,
          startDate,
          endDate,
          staffId: staff.id!,
          staffName: staff.name,
          email: staff.email,
          employeeGrossReceipts: empGross,
          sharePercentage,
          allocatedShortfall,
          allocationMethod: 'gross_receipts',
          createdAt: new Date().toISOString(),
          batchId,
          source: 'Short-Fall',
          type: 'true_up',
          reportingStatus: 'true_up_allocated'
        });
      });
    } else {
      // Fallback to equal split if no gross receipts are defined
      const eligibleStaff = staffList.filter(s => (reportedTipsByStaff[s.id!] || 0) > 0);
      if (eligibleStaff.length > 0) {
        eligibleStaff.forEach(staff => {
          const sharePercentage = 1 / eligibleStaff.length;
          const allocatedShortfall = shortfall * sharePercentage;
          
          allocations.push({
            tenantId,
            startDate,
            endDate,
            staffId: staff.id!,
            staffName: staff.name,
            email: staff.email,
            sharePercentage,
            allocatedShortfall,
            allocationMethod: 'good_faith',
            createdAt: new Date().toISOString(),
            batchId,
            source: 'Short-Fall',
            type: 'true_up',
            reportingStatus: 'true_up_allocated'
          });
        });
      }
    }
  } else {
    // Good Faith: Equal Split among tipped employees with transactions in period
    const eligibleStaff = staffList.filter(s => (reportedTipsByStaff[s.id!] || 0) > 0);
    if (eligibleStaff.length > 0) {
      eligibleStaff.forEach(staff => {
        const sharePercentage = 1 / eligibleStaff.length;
        const allocatedShortfall = shortfall * sharePercentage;
        
        allocations.push({
          tenantId,
          startDate,
          endDate,
          staffId: staff.id!,
          staffName: staff.name,
          email: staff.email,
          sharePercentage,
          allocatedShortfall,
          allocationMethod: 'good_faith',
          createdAt: new Date().toISOString(),
          batchId,
          source: 'Short-Fall',
          type: 'true_up',
          reportingStatus: 'true_up_allocated'
        });
      });
    }
  }
  
  return {
    totalGrossReceipts,
    totalChargeReceipts,
    totalChargeTips,
    targetTips,
    totalReportedTips,
    shortfall,
    allocations: allocations.sort((a, b) => b.allocatedShortfall - a.allocatedShortfall)
  };
};

export const saveShortfallAllocations = async (tenantId: string, allocations: ShortfallAllocation[]): Promise<void> => {
  const batch = writeBatch(db);
  const colRef = collection(db, 'tenants', tenantId, 'shortfall_allocations');
  const masterRef = collection(db, 'tenants', tenantId, 'master_transactions');
  
  allocations.forEach(alloc => {
    const ref = doc(colRef);
    const masterDocRef = doc(masterRef);
    
    const shortfallId = ref.id;
    const masterId = masterDocRef.id;

    // 1. Save in shortfall_allocations collection
    batch.set(ref, {
      ...alloc,
      id: shortfallId,
      masterTxId: masterId,
      createdAt: serverTimestamp()
    });

    // 2. Save in master_transactions collection (Option A Unified Ledger integration)
    batch.set(masterDocRef, {
      id: masterId,
      tenantId: tenantId,
      source: alloc.source || 'Short-Fall',
      type: alloc.type || 'true_up',
      reportingStatus: alloc.reportingStatus || 'true_up_allocated',
      amount: alloc.allocatedShortfall,
      tipDate: alloc.endDate, // End date of period represents operational date for the range filter
      staffId: alloc.staffId,
      staffName: alloc.staffName,
      email: alloc.email,
      submissionStatus: 'Approved',
      allocationStatus: 'Finalized',
      createdAt: serverTimestamp(),
      shortfallAllocId: shortfallId,
      shift: 'Shortfall Allocation',
      note: `IRS 8% Safe Harbor Shortfall Allocation for period ${alloc.startDate} to ${alloc.endDate}`,
      batchId: alloc.batchId || '',
      periodStartDate: alloc.startDate,
      periodEndDate: alloc.endDate
    });
  });
  
  await batch.commit();
};

export const getShortfallAllocationsForPeriod = async (tenantId: string, startDate: string, endDate: string): Promise<ShortfallAllocation[]> => {
  const colRef = collection(db, 'tenants', tenantId, 'shortfall_allocations');
  const q = query(
    colRef,
    where('startDate', '==', startDate),
    where('endDate', '==', endDate)
  );
  
  try {
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ShortfallAllocation));
  } catch (error) {
    console.error('Error fetching shortfall allocations:', error);
    return [];
  }
};

export const getShortfallAllocationsByStaffId = async (tenantId: string, staffId: string): Promise<ShortfallAllocation[]> => {
  const colRef = collection(db, 'tenants', tenantId, 'shortfall_allocations');
  const q = query(colRef, where('staffId', '==', staffId));
  try {
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ShortfallAllocation));
  } catch (error) {
    console.error('Error fetching shortfall allocations by staffId:', error);
    return [];
  }
};

export const getAllShortfallAllocations = async (tenantId: string): Promise<ShortfallAllocation[]> => {
  const colRef = collection(db, 'tenants', tenantId, 'shortfall_allocations');
  try {
    const snapshot = await getDocs(colRef);
    const results = snapshot.docs.map(doc => {
      const data = doc.data();
      let createdStr = '';
      if (data.createdAt) {
        if (data.createdAt.toDate) {
          createdStr = data.createdAt.toDate().toISOString();
        } else {
          createdStr = String(data.createdAt);
        }
      }
      return { 
        id: doc.id, 
        ...data,
        createdAt: createdStr || undefined
      } as ShortfallAllocation;
    });
    // Sort locally in descending order of createdAt, falling back to startDate
    return results.sort((a, b) => {
      const timeA = a.createdAt ? new Date(a.createdAt).getTime() : (a.startDate ? new Date(a.startDate).getTime() : 0);
      const timeB = b.createdAt ? new Date(b.createdAt).getTime() : (b.startDate ? new Date(b.startDate).getTime() : 0);
      return timeB - timeA;
    });
  } catch (error) {
    console.error('Error fetching all shortfall allocations:', error);
    return [];
  }
};

export const approveShortfallAllocationByEmployee = async (
  tenantId: string,
  allocId: string,
  signatureType: 'type' | 'draw',
  signatureText?: string,
  signatureDrawn?: string
) => {
  console.log(`DEBUG: Approving Shortfall ${allocId} in Firestore...`);
  const docRef = doc(db, 'tenants', tenantId, 'shortfall_allocations', allocId);
  await updateDoc(docRef, {
    employeeApproved: true,
    employeeApprovalTimestamp: serverTimestamp(),
    employeeApprovalSignatureType: signatureType,
    employeeApprovalSignatureText: signatureText || null,
    employeeApprovalSignatureDrawn: signatureDrawn || null
  });
  console.log(`DEBUG: Shortfall ${allocId} approved successfully in Firestore.`);
};
